import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  Spin,
  Typography,
  List,
  Divider,
  Modal,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  RobotOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { mekakApi, formatDate, formatCost } from './services';
import type { Knowledge, KnowledgeImage } from './models';
import ImageGallery from './components/ImageGallery';

const { Title, Text } = Typography;
const { confirm } = Modal;

const KnowledgeDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [knowledge, setKnowledge] = useState<Knowledge | null>(null);
  const [images, setImages] = useState<KnowledgeImage[]>([]);
  const [imagesLoading, setImagesLoading] = useState(false);

  const fetchKnowledge = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const response = await mekakApi.getKnowledge(id);
      if (response.data.success && response.data.data) {
        setKnowledge(response.data.data);
      } else {
        message.error('找不到此知識條目');
        navigate('/mekak');
      }
    } catch (error) {
      message.error('獲取知識資料失敗，請稍後再試');
      navigate('/mekak');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  // Fetch images for file-sourced knowledge
  useEffect(() => {
    if (!id || !knowledge) return;
    const k = knowledge as Knowledge & { source_type?: string };
    if (k.source_type !== 'file') return;

    setImagesLoading(true);
    mekakApi
      .getKnowledgeImages(id)
      .then((res) => {
        if (res.data?.success && res.data.data) {
          setImages(res.data.data);
        }
      })
      .catch(() => { /* silently ignore */ })
      .finally(() => setImagesLoading(false));
  }, [id, knowledge]);

  useEffect(() => {
    fetchKnowledge();
  }, [fetchKnowledge]);

  const handleEdit = () => {
    navigate(`/mekak/knowledge/${id}/edit`);
  };

  const handleDelete = () => {
    confirm({
      title: '確認刪除',
      content: `確定要刪除「${knowledge?.title}」嗎？此操作無法復原。`,
      okText: '確認刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        if (!id) return;
        try {
          await mekakApi.deleteKnowledge(id);
          message.success('知識條目已成功刪除');
          navigate('/mekak');
        } catch (error) {
          message.error('刪除失敗，請稍後再試');
        }
      },
    });
  };

  const handleBackToList = () => {
    navigate('/mekak');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Spin size="large" tip="載入中..." />
      </div>
    );
  }

  if (!knowledge) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 0' }}>
        <Text type="secondary">找不到此知識條目</Text>
      </div>
    );
  }

  const hasPreprocessedMetadata =
    knowledge.metadata != null && knowledge.metadata.preprocessed === true;

  return (
    <div className="p-6">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            返回列表
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {knowledge.title}
          </Title>
        </Space>
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={handleEdit}
          >
            編輯
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={handleDelete}
          >
            刪除
          </Button>
        </Space>
      </div>

      {/* Basic Information */}
      <Card title="基本資訊" className="mb-4">
        <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }}>
          <Descriptions.Item label="標題" span={2}>
            <Text strong>{knowledge.title}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="作者">
            {knowledge.author || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="瀏覽次數">
            {knowledge.view_count ?? 0}
          </Descriptions.Item>
          <Descriptions.Item label="建立時間">
            {formatDate(knowledge.created_at)}
          </Descriptions.Item>
          <Descriptions.Item label="更新時間">
            {formatDate(knowledge.updated_at)}
          </Descriptions.Item>
          <Descriptions.Item label="標籤" span={2}>
            {knowledge.tags && knowledge.tags.length > 0 ? (
              <Space wrap>
                {knowledge.tags.map((tag) => (
                  <Tag key={tag} color="blue">
                    {tag}
                  </Tag>
                ))}
              </Space>
            ) : (
              <Text type="secondary">（無標籤）</Text>
            )}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Content */}
      <Card title="知識內容" className="mb-4">
        <div
          style={{
            whiteSpace: 'pre-wrap',
            lineHeight: '1.8',
            fontSize: '14px',
            color: 'rgba(0, 0, 0, 0.88)',
            padding: '8px 0',
          }}
        >
          {knowledge.content || <Text type="secondary">（無內容）</Text>}
        </div>
      </Card>

      {/* Images from file source */}
      {(images.length > 0 || imagesLoading) && (
        <Card title="相關圖片" className="mb-4">
          <ImageGallery images={images} loading={imagesLoading} />
        </Card>
      )}

      {/* AI Preprocessing Metadata */}
      {hasPreprocessedMetadata && knowledge.metadata && (
        <Card
          title={
            <Space>
              <RobotOutlined />
              <span>AI 預處理資訊</span>
            </Space>
          }
          className="mb-4"
        >
          <Descriptions bordered column={{ xs: 1, sm: 1, md: 2 }}>
            <Descriptions.Item label="主題">
              {knowledge.metadata.main_topic || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="片段數量">
              {knowledge.metadata.chunk_count ?? '-'}
            </Descriptions.Item>
          </Descriptions>

          {knowledge.metadata.potential_queries &&
            knowledge.metadata.potential_queries.length > 0 && (
              <>
                <Divider orientation="left" plain>
                  潛在查詢問題
                </Divider>
                <List
                  size="small"
                  dataSource={knowledge.metadata.potential_queries}
                  renderItem={(query, index) => (
                    <List.Item>
                      <Text>
                        <Text type="secondary" style={{ marginRight: 8 }}>
                          {index + 1}.
                        </Text>
                        {query}
                      </Text>
                    </List.Item>
                  )}
                />
              </>
            )}

          <Divider orientation="left" plain>
            <Space>
              <DollarOutlined />
              <span>成本追蹤</span>
            </Space>
          </Divider>
          <Descriptions bordered column={{ xs: 1, sm: 1, md: 3 }}>
            <Descriptions.Item label="LLM Tokens">
              {knowledge.metadata.llm_tokens != null
                ? knowledge.metadata.llm_tokens.toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="向量化字符數">
              {knowledge.metadata.embedding_chars != null
                ? knowledge.metadata.embedding_chars.toLocaleString()
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="總成本">
              <Text strong style={{ color: '#52c41a' }}>
                {knowledge.metadata.total_cost != null
                  ? formatCost(knowledge.metadata.total_cost)
                  : '-'}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {/* Bottom Action Bar */}
      <div className="flex justify-end mt-4">
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={handleBackToList}>
            返回列表
          </Button>
          <Button type="primary" icon={<EditOutlined />} onClick={handleEdit}>
            編輯
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
            刪除
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default KnowledgeDetail;
